"use client";

import { useState } from "react";
import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ImageIcon from "lucide-react/dist/esm/icons/image";
import Briefcase from "lucide-react/dist/esm/icons/briefcase";
import X from "lucide-react/dist/esm/icons/x";
import Tag from "lucide-react/dist/esm/icons/tag";
import { commissionsHref } from "@/lib/commissions/routes";

interface Props {
  sellerId: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  Thumbnail: "bg-pink-500/15 text-pink-400 border-pink-500/20",
  "3D Model": "bg-blue-500/15 text-blue-400 border-blue-500/20",
  Map: "bg-success/15 text-success border-success/20",
  Code: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  "UI/UX": "bg-purple-500/15 text-purple-400 border-purple-500/20",
  Animation: "bg-primary/15 text-primary border-primary/20",
  Other: "bg-muted text-muted-foreground border-border",
};

export function PortfolioGrid({ sellerId }: Props) {
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const { data: items, isLoading } = useQuery({
    queryKey: queryKeys.commissions.portfolio(sellerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("commission_portfolio" as any)
        .select("*")
        .eq("seller_id", sellerId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data as any[]) || [];
    },
    enabled: !!sellerId,
  });

  if (isLoading) {
    return (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-64 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!items?.length) {
    return (
      <EmptyState
        icon={Briefcase}
        title="No portfolio items yet"
        description="This seller has not added commission portfolio samples."
        action={{ label: "Browse commissions", href: commissionsHref.creators, variant: "outline" }}
      />
    );
  }

  // Extract unique categories
  const categories = [...new Set(items.map((i: any) => i.category).filter(Boolean))];
  const filtered = categoryFilter
    ? items.filter((i: any) => i.category === categoryFilter)
    : items;

  return (
    <div className="space-y-4">
      {/* Category filter pills */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={categoryFilter === null ? "default" : "ghost"}
            size="sm"
            className="h-7 text-xs rounded-full"
            onClick={() => setCategoryFilter(null)}
          >
            All ({items.length})
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={categoryFilter === cat ? "default" : "ghost"}
              size="sm"
              className="h-7 text-xs rounded-full"
              onClick={() => setCategoryFilter(cat === categoryFilter ? null : cat)}
            >
              {cat} ({items.filter((i: any) => i.category === cat).length})
            </Button>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((item: any) => (
          <Card
            key={item.id}
            className="glass border-border/50 overflow-hidden group hover-lift cursor-pointer"
            onClick={() => setSelectedItem(item)}
          >
            {item.image_urls?.[0] ? (
              <div className="relative aspect-video overflow-hidden">
                <Image
                  src={item.image_urls[0]}
                  alt={item.title}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="aspect-video bg-muted/50 flex items-center justify-center">
                <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
              </div>
            )}
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-semibold text-sm line-clamp-1">{item.title}</h3>
                {item.category && (
                  <Badge variant="outline" className={`text-2xs shrink-0 ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS.Other}`}>
                    {item.category}
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
              )}
              {item.client_name && (
                <p className="text-2xs text-muted-foreground">Made for <span className="font-medium text-foreground">{item.client_name}</span></p>
              )}
              {item.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 4).map((tag: string) => (
                    <span key={tag} className="text-2xs bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded-full">
                      {tag}
                    </span>
                  ))}
                  {item.tags.length > 4 && (
                    <span className="text-2xs text-muted-foreground">+{item.tags.length - 4}</span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {selectedItem && (
            <>
              {selectedItem.image_urls?.[0] ? (
                <div className="relative aspect-video bg-black">
                  <Image
                    src={selectedItem.image_urls[0]}
                    alt={selectedItem.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 672px"
                    className="object-contain"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-muted/30 flex items-center justify-center">
                  <ImageIcon className="h-16 w-16 text-muted-foreground/30" />
                </div>
              )}
              <div className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display font-bold text-lg">{selectedItem.title}</h2>
                  {selectedItem.category && (
                    <Badge variant="outline" className={CATEGORY_COLORS[selectedItem.category] || CATEGORY_COLORS.Other}>
                      {selectedItem.category}
                    </Badge>
                  )}
                </div>
                {selectedItem.description && (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedItem.description}</p>
                )}
                {selectedItem.client_name && (
                  <p className="text-xs text-muted-foreground">Made for <span className="font-medium text-foreground">{selectedItem.client_name}</span></p>
                )}
                {selectedItem.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedItem.tags.map((tag: string) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        <Tag className="h-2.5 w-2.5 mr-1" />{tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
